import * as Updates from "expo-updates";

import { AppVariant, getAppVariant } from "./expo";

export enum Environment {
  DEVELOPMENT = "development",
  TEST = "test",
  PRODUCTION = "production",
}

export type StreamEnvVariables = {
  chatApiKey: string;
};

export type SentryEnvVariables = {
  dns: string;
};

export type RevenueCatEnvVariables = {
  iosPublicKey: string;
  androidPublicKey: string;
};

export type FireBaseEnvVariables = {
  projectId: string;
  androidAppId: string;
  iosAppId: string;
};

export type EnvironmentVariables = {
  environment: Environment;
  sentry: SentryEnvVariables;
  revenueCat: RevenueCatEnvVariables;
  firebase: FireBaseEnvVariables;
  stream: StreamEnvVariables;
  baseApiUrl: string;
  apiClientId: string;
  ipApi: string;
};

class EnvironmentVariablesLoader {
  public static getEnvironmentVariables(
    env?: Environment,
  ): EnvironmentVariables {
    const environment = env || EnvironmentVariablesLoader.getEnvironment();

    // Revenue Cat keys are based on the app variant not the environment. Each key corresponds to a specific app bundleId.
    let iosPublicKey = "appl_mmrtBhNdQYPddFxtMTluUDCqGRB";
    let androidPublicKey = "goog_rNzkkZpvKYQTyeLiISaRpuzFcNJ";

    switch (getAppVariant()) {
      case AppVariant.PRODUCTION: {
        iosPublicKey = "appl_mmrtBhNdQYPddFxtMTluUDCqGRB";
        androidPublicKey = "goog_rNzkkZpvKYQTyeLiISaRpuzFcNJ";
        break;
      }
      case AppVariant.PREVIEW: {
        iosPublicKey = "appl_BRdBdMTKwYUzNJlYCAqDHyiYfBj"; // cspell:disable-line
        androidPublicKey = "goog_htmKapRBTuttRdebVWseWWAxtuk"; // cspell:disable-line
        break;
      }
      case AppVariant.DEVELOPMENT: {
        iosPublicKey = "appl_sfkVdKyTSwOwWitAwtWYMFqKVDY"; // cspell:disable-line
        androidPublicKey = "goog_aVhZgIfTdjmSyqbKhhfQNCZlWYF"; // cspell:disable-line
        break;
      }
      case AppVariant.SIMULATOR: {
        iosPublicKey = "test_UQxSMfPKmFIqSjyvBjXPzaszWVg"; // cspell:disable-line
        androidPublicKey = "test_UQxSMfPKmFIqSjyvBjXPzaszWVg"; // cspell:disable-line
        break;
      }
    }

    switch (environment) {
      // Todo; these values need to move to expo env variables.
      case Environment.DEVELOPMENT: {
        return {
          environment: Environment.DEVELOPMENT,
          baseApiUrl: "https://lambda-staging.getkaiapp.com",
          apiClientId: "8501be13b28fe8ed44c84e858ccb7e66",
          sentry: {
            dns: "https://a419a447eb22f38f047ba1e82fac83dd@o4506895076229120.ingest.us.sentry.io/4506900449001472",
          },
          revenueCat: {
            iosPublicKey: iosPublicKey,
            androidPublicKey: androidPublicKey,
          },
          stream: {
            chatApiKey: "sykb2jkefmrn",
          },
          firebase: {
            projectId: "kai-social-dev-c5e95",
            androidAppId: "1:617204377937:android:c3a73f3019f4a86c234b70",
            iosAppId: "1:617204377937:ios:2af39d4c6ed30918234b70",
          },
          ipApi: "wKmiaSfcmj7MJGLEo7tVdEWvlspmOrVejdPmbatPi29aBgaZlM",
        };
      }
      case Environment.TEST: {
        return {
          environment: Environment.TEST,
          baseApiUrl: "https://lambda-staging.getkaiapp.com",
          apiClientId: "8501be13b28fe8ed44c84e858ccb7e66",
          sentry: {
            dns: "https://a419a447eb22f38f047ba1e82fac83dd@o4506895076229120.ingest.us.sentry.io/4506900449001472",
          },
          revenueCat: {
            iosPublicKey: iosPublicKey,
            androidPublicKey: androidPublicKey,
          },
          stream: {
            chatApiKey: "sykb2jkefmrn",
          },
          firebase: {
            projectId: "kai-social-dev-c5e95",
            androidAppId: "1:617204377937:android:c3a73f3019f4a86c234b70",
            iosAppId: "1:617204377937:ios:2af39d4c6ed30918234b70",
          },
          ipApi: "wKmiaSfcmj7MJGLEo7tVdEWvlspmOrVejdPmbatPi29aBgaZlM",
        };
      }
      case Environment.PRODUCTION: {
        return {
          environment: Environment.PRODUCTION,
          baseApiUrl: "https://lambda-prod.getkaiapp.com",
          apiClientId: "b90b735ba4436df684e4786820047c94",
          sentry: {
            dns: "https://a419a447eb22f38f047ba1e82fac83dd@o4506895076229120.ingest.us.sentry.io/4506900449001472",
          },
          revenueCat: {
            iosPublicKey: iosPublicKey,
            androidPublicKey: androidPublicKey,
          },
          stream: {
            chatApiKey: "fg4j7wqgwrtw",
          },
          firebase: {
            projectId: "kai-social",
            androidAppId: "1:763623495611:android:d53cf65c1f16f6dac4169e",
            iosAppId: "1:763623495611:ios:a6da0d8143feb7dbc4169e",
          },
          ipApi: "wKmiaSfcmj7MJGLEo7tVdEWvlspmOrVejdPmbatPi29aBgaZlM",
        };
      }
    }
  }

  public static getEnvironmentByNode(): Environment {
    const node_env = process.env.NODE_ENV;
    // console.log("node_env: ", node_env)

    switch (node_env) {
      case "development": {
        return Environment.DEVELOPMENT;
      }
      case "test": {
        return Environment.TEST;
      }
      case "production": {
        return Environment.PRODUCTION;
      }
      default:
        return Environment.PRODUCTION;
    }
  }
  public static getEnvironmentByChannel(): Environment {
    const channel = Updates.channel || AppVariant.UNKNOWN;
    switch (channel) {
      case AppVariant.PRODUCTION: {
        return Environment.PRODUCTION;
      }
      case AppVariant.PREVIEW: {
        return Environment.PRODUCTION;
      }
      case AppVariant.DEVELOPMENT: {
        return Environment.TEST;
      }
      case AppVariant.SIMULATOR: {
        return Environment.TEST;
      }
      default: {
        return EnvironmentVariablesLoader.getEnvironmentByNode();
      }
    }
  }

  public static getEnvironment(): Environment {
    // We need the ability to switch the environment on the app.
    const env = EnvironmentVariablesLoader.getEnvironmentByChannel();
    return env;
  }

  public static isProduction(): boolean {
    return (
      EnvironmentVariablesLoader.getEnvironment() === Environment.PRODUCTION
    );
  }

  public static validate(environmentVariables: EnvironmentVariables): void {
    const validators = {
      EXPO_PUBLIC_BASE_API_URL: (value: string): void => {
        try {
          new URL(value);
        } catch {
          throw new Error(`Invalid EXPO_PUBLIC_BASE_API_URL=${value}`);
        }
      },
      EXPO_PUBLIC_SENTRY_DNS: (value: string): void => {
        // Check if the value is a valid URL
        try {
          new URL(value);
        } catch {
          throw new Error(`Invalid EXPO_PUBLIC_SENTRY_DNS=${value} URL`);
        }
      },
      EXPO_PUBLIC_STREAM_CHAT_API_KEY: (value: string): void => {
        // Check if the value is not empty
        if (!value) {
          throw new Error("EXPO_PUBLIC_STREAM_CHAT_API_KEY cannot be empty");
        }
        const regex = /^[\w-]+$/;
        if (!regex.test(value)) {
          throw new Error(
            `Invalid EXPO_PUBLIC_STREAM_CHAT_API_KEY=${value} format`,
          );
        }
      },
    };

    validators["EXPO_PUBLIC_SENTRY_DNS"](environmentVariables.sentry.dns);
    validators["EXPO_PUBLIC_STREAM_CHAT_API_KEY"](
      environmentVariables.stream.chatApiKey,
    );
    validators["EXPO_PUBLIC_BASE_API_URL"](environmentVariables.baseApiUrl);
  }
}

export const ENV_VARIABLES =
  EnvironmentVariablesLoader.getEnvironmentVariables();
export default EnvironmentVariablesLoader;
